customElements.get("product-info") || customElements.define("product-info", class extends HTMLElement {
    lastIsMobile = null;
    abortController = void 0;
    onVariantChangeUnsubscriber = void 0;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];
    cartUpdateUnsubscriber = void 0;
    userSelectedVariantId = null;

    constructor() {
        super();
    }

    get variantSelectors() { return this.querySelector("variant-selects"); }
    get productId() { return this.getAttribute("data-product-id"); }
    get sectionId() { return this.dataset.originalSection || this.dataset.section; }
    get productForm() { return this.querySelector('form[is="product-form"]'); }
    get quantityInput() { return this.querySelector("quantity-input input"); }

    connectedCallback() {
        this.initializeProductSwapUtility();
        
        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, 
            this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.lastIsMobile = window.innerWidth < 768;
        this._resizeHandler = () => {
            const t = window.innerWidth < 768;
            t !== this.lastIsMobile && (this.lastIsMobile = t, window.location.reload());
        };
        window.addEventListener("resize", this._resizeHandler);

        this.initFormValidation();
    }

    // Formata preço em pt-BR — aceita tanto centavos inteiros (ex: 3990)
    // quanto string decimal do .json (ex: "39.90")
    centsToFormatted(price) {
        const value = typeof price === 'string' ? parseFloat(price) : price / 100;
        return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // Atualiza o texto de um elemento de preço preservando o prefixo de moeda
    // O prefixo é tudo antes do primeiro dígito (ex: "R$ " ou "R$\u00a0")
    setPriceText(el, cents) {
        if (!el) return;
        const text = el.textContent;
        // Encontra onde começa o primeiro dígito
        const firstDigitIdx = text.search(/\d/);
        const prefix = firstDigitIdx >= 0 ? text.slice(0, firstDigitIdx) : 'R$ ';
        el.textContent = prefix + this.centsToFormatted(cents);
    }

    updatePriceFromVariant(variant) {
        const priceEl = this.querySelector('[id*="price"]');
        if (!priceEl || !variant) return;

        const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

        // Preço de venda (span com classe --sale)
        const salePriceEl = priceEl.querySelector('.f-price-item--sale');
        if (salePriceEl) {
            this.setPriceText(salePriceEl, variant.price);
        }

        // Preço riscado (compare_at_price) — está dentro de <s> no bloco .f-price__sale
        const sTag = priceEl.querySelector('.f-price__sale s');
        if (sTag) {
            if (hasDiscount) {
                this.setPriceText(sTag, variant.compare_at_price);
                sTag.style.display = '';
            } else {
                sTag.style.display = 'none';
            }
        }

        // Preço normal quando não há desconto
        const regularPriceEl = priceEl.querySelector('.f-price__regular .f-price-item--regular');
        if (regularPriceEl) {
            this.setPriceText(regularPriceEl, variant.price);
        }

        // Toggle classe on-sale no wrapper
        const priceWrapper = priceEl.querySelector('.f-price');
        if (priceWrapper) {
            priceWrapper.classList.toggle('f-price--on-sale', !!hasDiscount);
        }
    }

    getSelectedOptionTitles(changedTarget = null) {
        const titles = [];
        const variantPicker = this.querySelector('variant-selects');
        if (!variantPicker) return titles;

        variantPicker.querySelectorAll('fieldset, .product-form__input').forEach(group => {
            // Se o target clicado está neste grupo, usa o valor novo diretamente
            if (changedTarget && group.contains(changedTarget)) {
                const val = changedTarget.dataset.optionValue
                    || changedTarget.value
                    || changedTarget.textContent.trim();
                if (val) { titles.push(val); return; }
            }

            const pressedBtn = group.querySelector('[aria-pressed="true"]');
            if (pressedBtn) {
                titles.push(pressedBtn.dataset.optionValue || pressedBtn.textContent.trim());
                return;
            }
            const checkedInput = group.querySelector('input[type="radio"]:checked');
            if (checkedInput) {
                titles.push(checkedInput.value);
                return;
            }
            const select = group.querySelector('select');
            if (select) titles.push(select.value);
        });

        return titles;
    }

    resolveVariantFromSelectedOptions(productUrl, changedTarget = null) {
        const handle = productUrl.split('/products/')[1]?.split('?')[0];
        if (!handle) return Promise.resolve(null);

        const selectedTitles = this.getSelectedOptionTitles(changedTarget);
        if (!selectedTitles.length) return Promise.resolve(null);

        return fetch(`/products/${handle}.js`)
            .then(r => r.json())
            .then(data => {
                const variants = data?.variants || [];
                const match = variants.find(v => {
                    const variantOptions = [v.option1, v.option2, v.option3]
                        .filter(Boolean)
                        .map(s => s.toLowerCase().trim());
                    return selectedTitles.every(title =>
                        variantOptions.includes(title.toLowerCase().trim())
                    );
                });
                return match || null;
            })
            .catch(() => null);
    }

    getActiveVariantId() {
        if (this.userSelectedVariantId) return String(this.userSelectedVariantId);

        const input = this.productForm?.querySelector('input[name="id"]');
        if (input?.value && input.value !== "0") return input.value;

        const selectedVariantEl = this.querySelector('[data-selected-variant]');
        if (selectedVariantEl?.innerHTML) {
            try {
                const parsed = JSON.parse(selectedVariantEl.innerHTML);
                if (parsed?.id) return String(parsed.id);
            } catch(e) {}
        }

        return null;
    }

    isEventRelevant(event, target) {
        if (this.contains(event.target)) return true;

        const targetProductUrl = target.dataset.productUrl || '';
        const thisProductUrl = this.dataset.url || '';
        if (targetProductUrl && thisProductUrl) {
            const targetHandle = targetProductUrl.split('/products/')[1]?.split('?')[0];
            const thisHandle = thisProductUrl.split('/products/')[1]?.split('?')[0];
            if (targetHandle && thisHandle && targetHandle === thisHandle) return true;
        }

        const targetSection = target.closest('[data-section]')?.dataset.section
                           || target.closest('product-info')?.dataset.section;
        if (targetSection && targetSection === this.dataset.section) return true;

        return false;
    }

    initFormValidation() {
        const t = this.productForm;
        if (!t) return;

        const hasVariantPicker = !!this.querySelector('variant-selects');

        if (!hasVariantPicker) {
            const variantInput = t.querySelector('input[name="id"]');

            if (variantInput && (!variantInput.value || variantInput.value === "0")) {
                const selectedVariantEl = this.querySelector('[data-selected-variant]');
                if (selectedVariantEl) {
                    try {
                        const parsed = JSON.parse(selectedVariantEl.innerHTML);
                        if (parsed?.id) variantInput.value = String(parsed.id);
                    } catch(e) {}
                }

                if (!variantInput.value || variantInput.value === "0") {
                    const productUrl = this.dataset.url || window.location.pathname;
                    const handle = productUrl.split('/products/')[1]?.split('?')[0];
                    if (handle) {
                        fetch(`/products/${handle}.js`)
                            .then(r => r.json())
                            .then(data => {
                                const firstVariantId = data?.variants?.[0]?.id;
                                if (firstVariantId) variantInput.value = String(firstVariantId);
                            })
                            .catch(() => {});
                    }
                }
            }

            const btn = t.querySelector('[name="add"]');
            if (btn) btn.removeAttribute("disabled");

            const existingError = t.querySelector(".variant-error");
            if (existingError) existingError.remove();

            return;
        }

        this._submitValidated = false;

        t.addEventListener("submit", (e) => {
            if (this._submitValidated) {
                this._submitValidated = false;
                return;
            }

            e.preventDefault();
            e.stopImmediatePropagation();

            const productUrl = this.dataset.url || window.location.pathname;
            const variantInput = t.querySelector('input[name="id"]');

            this.resolveVariantFromSelectedOptions(productUrl).then(resolvedVariant => {
                const activeId = resolvedVariant?.id
                    ? String(resolvedVariant.id)
                    : this.getActiveVariantId();

                if (!activeId) {
                    if (!t.querySelector(".variant-error")) {
                        const errorDiv = document.createElement("div");
                        errorDiv.className = "variant-error";
                        errorDiv.style.cssText = "color: red; margin: 8px 0; font-weight: bold; display: block;";
                        errorDiv.textContent = "Selecione uma variação antes de adicionar ao carrinho.";
                        t.prepend(errorDiv);
                    }
                    return;
                }

                const error = t.querySelector(".variant-error");
                if (error) error.remove();

                if (variantInput) variantInput.value = activeId;
                this.userSelectedVariantId = activeId;

                this._submitValidated = true;
                t.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
            });
        }, { capture: true });
    }

    disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener("resize", this._resizeHandler);
    }

    initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((t => {
            window?.Shopify?.PaymentButton?.init();
            window?.ProductModel?.loadShopifyXR();
        }));
    }

    handleOptionValueChange({ data: { event: t, target: e, selectedOptionValues: i } }) {
        if (!this.isEventRelevant(t, e)) return;

        const s = e.dataset.productUrl || this.pendingRequestUrl || this.dataset.url,
            n = this.dataset.url !== s,
            a = "true" === this.dataset.updateUrl && n,
            r = this.dataset.viewMode || "main-product";

        // Atualiza preço e galeria imediatamente ao trocar de opção
        this.resolveVariantFromSelectedOptions(s, e).then(variant => {
            if (variant) {
                this.updatePriceFromVariant(variant);
                this.userSelectedVariantId = variant.id;

                const localInput = this.productForm?.querySelector('input[name="id"]');
                if (localInput) {
                    localInput.value = String(variant.id);
                    localInput.dispatchEvent(new Event("change", { bubbles: true }));
                }

                // Atualiza galeria com o variant que contém featured_media
                // Preserva scroll pois setActiveMedia/sortMediaItems chama window.scrollTo internamente
                if (variant.featured_media?.id) {
                    const gallery = document.querySelector('media-gallery');
                    if (gallery) {
                        const scrollX = window.scrollX;
                        const scrollY = window.scrollY;
                        const origScrollTo = window.scrollTo.bind(window);
                        window.scrollTo = () => {};
                        try {
                            if (typeof gallery.setActiveMedia === 'function') {
                                gallery.setActiveMedia(variant);
                            } else if (typeof gallery.filterMediasByVariant === 'function') {
                                gallery.filterMediasByVariant(variant);
                            }
                        } finally {
                            window.scrollTo = origScrollTo;
                            window.scrollTo(scrollX, scrollY);
                        }
                    }
                }
            }
        });

        this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(s, i, a),
            targetId: e.id,
            callback: n ? this.handleSwapProduct(s, a, r) : this.handleUpdateProductInfo(s, r, e)
        });
    }

    handleSwapProduct(t, e, i) {
        return s => {
            const a = e ? "product-info[id^='MainProduct']" : "product-info",
                r = this.getSelectedVariant(s.querySelector(a));
            this.updateURL(t, r?.id);
            HTMLUpdateUtility.viewTransition(this, s.querySelector("product-info"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
            this.currentVariant = r;
            if (r?.id) this.userSelectedVariantId = r.id;
        }
    }

    handleUpdateProductInfo(t, e, i = null) {
        return s => {
            const a = this.getSelectedVariant(s);
            this.currentVariant = a;
            if (a?.id) this.userSelectedVariantId = a.id;

            this.updateOptionValues(s, i);
            this.updateURL(t, a?.id);
            this.updateVariantInputs(a?.id);

            if (!a) {
                const variantSelects = s.querySelector("variant-selects");
                if (variantSelects) return this.setUnavailable();
                return;
            }

            const priceUpdated = (() => {
                const src = s.getElementById(`price-${this.sectionId}`);
                const dest = this.querySelector(`#price-${this.dataset.section}`);
                if (src && dest) { dest.innerHTML = src.innerHTML; return true; }
                return false;
            })();

            if (!priceUpdated) this.updatePriceFromVariant(a);

            const inventorySrc = s.getElementById(`Inventory-${this.sectionId}`);
            const inventoryDest = this.querySelector(`#Inventory-${this.dataset.section}`);
            if (inventorySrc && inventoryDest) inventoryDest.innerHTML = inventorySrc.innerHTML;

            this.toggleAddButton(false);
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.sectionId, html: s, variant: a } });
        }
    }

    buildRequestUrlWithParams(t, e, i = !1) {
        const s = [];
        if(!i) s.push(`section_id=${this.sectionId}`);
        if(e.length) s.push(`option_values=${e.join(",")}`);
        return `${t}?${s.join("&")}`;
    }

    getSelectedVariant(t) {
        const e = t.querySelector("variant-selects [data-selected-variant]")?.innerHTML;
        return e ? JSON.parse(e) : null;
    }

    renderProductInfo({ requestUrl: t, targetId: e, callback: i }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        fetch(t, { signal: this.abortController.signal })
            .then(res => res.text())
            .then(text => {
                this.pendingRequestUrl = null;
                const html = (new DOMParser).parseFromString(text, "text/html");
                i(html);
            })
            .catch(err => console.error(err));
    }

    updateOptionValues(t, e = null) {
        const i = t.querySelector("variant-selects");
        if(i && this.variantSelectors) {
            HTMLUpdateUtility.viewTransition(this.variantSelectors, i, this.preProcessHtmlCallbacks, [newSection => {
                if (e) {
                    const input = newSection.querySelector(`input[value="${e.value}"]`);
                    if(input) input.checked = true;
                }
            }]);
        }
    }

    updateURL(t, e) {
        if(this.dataset.updateUrl !== "false") {
            window.history.replaceState({}, "", `${t}${e ? `?variant=${e}` : ""}`);
        }
    }

    updateVariantInputs(t) {
        const form = document.querySelector(`#product-form-${this.dataset.section}`);
        if(form) {
            const input = form.querySelector('input[name="id"]');
            if(input) {
                input.value = t ?? "";
                input.dispatchEvent(new Event("change", { bubbles: true }));
            }
        }

        const localInput = this.productForm?.querySelector('input[name="id"]');
        if (localInput && t) {
            localInput.value = String(t);
            localInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
    }

    toggleAddButton(disable = true, text) {
        const form = document.getElementById(`product-form-${this.dataset.section}`);
        if (!form) return;
        const btn = form.querySelector('[name="add"]'),
            label = form.querySelector('[name="add"] > span');
        if (!btn) return;
        
        if (disable) {
            btn.setAttribute("disabled", "disabled");
            if (text) label.textContent = this.decoded(text);
        } else {
            btn.removeAttribute("disabled");
            label.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }
    }

    decoded(t) {
        const e = document.createElement("div");
        e.innerHTML = t;
        return e.textContent;
    }

    setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
    }

    initQuantityHandlers() {
        if(this.quantityInput) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, { data: { sectionId: this.sectionId, productId: this.productId } });
        }
    }
});